import axios from "axios";
import JSZip from "jszip";
import memoizee from "memoizee";
import Papa from "papaparse";


import {
  LICENSE_CONSTANTS_URL,
  CREATOR_CONSTANTS_URL,
  CUSTOM_LICENSE_CONSTANTS_URL,
  DOMAINS_CONSTANTS_URL,
  FORMATS_CONSTANTS_URL,
  LANGUAGE_CONSTANTS_URL,
  MODEL_CONSTANTS_URL,
  TASK_CONSTANTS_URL,
} from "./constants";

import DatasetInfo from "../types/IDatasetInfo";

// Constants similar to `constants` module in the Python code
const CONSTANTS = {
  LICENSE_CONSTANTS_URL,
  CUSTOM_LICENSE_CONSTANTS_URL,
  LANGUAGE_CONSTANTS_URL,
  TASK_CONSTANTS_URL,
  DOMAINS_CONSTANTS_URL,
  MODEL_CONSTANTS_URL,
  CREATOR_CONSTANTS_URL,
  FORMATS_CONSTANTS_URL,
};

// Function to fetch and extract data summary JSON from a ZIP file on the server
// const readDataSummaryJson = memoizee(async function (
//   summaryDirUrl = "/data_summaries.zip",
// ) {
//   try {
//     // Fetch the ZIP file from the server
//     const response = await axios.get(summaryDirUrl, {
//       responseType: "arraybuffer",
//     });
//     const zip = await JSZip.loadAsync(response.data);

//     // Extract the 'data_summaries' folder
//     const dataSummariesFolder = zip.folder("data_summaries");
//     if (!dataSummariesFolder) {
//       throw new Error("Folder 'data_summaries' not found in the ZIP file");
//     }

//     const filePromises = [] as Promise<any>[];
//     dataSummariesFolder.forEach((relativePath, file) => {

//       // Read each file in the 'data_summaries' folder
//       if (
//         !relativePath.includes("_template.json") &&
//         !relativePath.includes("_template_spec.yaml")
//       ) {
//         filePromises.push(
//           file.async("string").then((content) => {
//             if (!content?.trim()) {
//               console.warn(`Skipped empty or invalid JSON in file: ${relativePath}`);
//               return null;
//             }

//             try {
//               return {
//                 relativePath,
//                 content: JSON.parse(content),
//               };
//             } catch (err) {
//               console.error(`Failed to parse JSON in file: ${relativePath}`, err);
//               return null;
//             }
//           })
//         );

//       }
//     });

//     const files = (await Promise.all(filePromises)).filter(Boolean);

//     const dataSummary: DatasetInfo[] = [];

//     files.forEach((file) => {
//       dataSummary.push(...extractValuesAsArray(file.content));
//     });

//     return dataSummary;
//   } catch (error) {
//     console.error("Error fetching or processing data summary:", error);
//     throw error;
//   }
// });

// Helper: Try parsing nested strings like '[...]' or '{...}'
function tryParseJSON(value: any) {
  if (!value || typeof value !== "string") return value;
  const cleaned = value.trim();
  if (
    (cleaned.startsWith("[") && cleaned.endsWith("]")) ||
    (cleaned.startsWith("{") && cleaned.endsWith("}"))
  ) {
    try {
      return JSON.parse(cleaned.replace(/'/g, '"'));
    } catch (err) {
      return value;
    }
  }
  return value;
}

// Helper: Extract values from object as an array
function extractValuesAsArray(obj: any) {
  const keys = Object.keys(obj);
  return keys.map((key) => obj[key]);
}

function mapCodesToKeys(array: string[], mappingObject: Record<string, string[]>): string[] {
  const result: string[] = [];

  array.forEach((value) => {
    for (const [key, values] of Object.entries(mappingObject)) {
      if (Array.isArray(values) && values.includes(value)) {
        result.push(key);
        break; // stop after first match
      }
    }
  });

  return result;
}


// Main CSV reader function
const readDataSummaryJson = memoizee(async function (
  csvUrl: string = "/data_summaries.csv"
) {
  try {
    const languageCodes = await axios.get("/html_constants/new-language-codes.json").then(res => res.data);
    
    const languageCodeMap: any = {};
    Object.entries(languageCodes).map((item:any ) => {
      const parts:any = item[1]?.split(";");

      parts.forEach((code:any) => {
        languageCodeMap[item[0]] = parts;
      });
    });
    
    
    const response = await axios.get(csvUrl, {
      responseType: "text",
    });

    const parsed = Papa.parse(response.data, {
      header: true,
      skipEmptyLines: true,
    });

    const headers = parsed.meta.fields || [];
    const results: Record<string, any[]> = {};

    parsed.data.forEach((row: any) => {
      const collectionName = row["Collection"]?.trim();
      const datasetKey = row["Unique Dataset Identifier"]?.trim();

      if (!collectionName || !datasetKey) return;

      const dataset: any = {};
      headers.forEach((key: any) => {
        dataset[key] = tryParseJSON(row[key]);
      });

      // Normalize fields
      let languages = tryParseJSON(dataset["Language (ISO)"]) || [];
      if (!Array.isArray(languages)) languages = [languages];
      // dataset["Languages"] = languages.map((code: string) => languageCodeMap[code] || code);
      dataset["Languages"] =  mapCodesToKeys(languages, languageCodeMap);
      delete dataset["Language (ISO)"];

      dataset["Task Categories"] = tryParseJSON(dataset["Task Groups"]) || [];
      delete dataset["Task Groups"];

      dataset["Dataset Filter IDs"] = [datasetKey];
      dataset["Format"] = dataset["Format"] || [];
      dataset["License Notes"] = dataset["License Notes"] || "";
      dataset["License Verified By"] = dataset["License Verified By"] || "";

     

      const wrapped = { [datasetKey]: dataset };

      if (!results[collectionName]) {
        results[collectionName] = [];
      }

      results[collectionName].push(wrapped);
    });

    // Convert each collection into merged structure (like JSON from ZIP)
    const dataSummary: any[] = [];

    Object.entries(results).forEach(([collectionName, datasets]) => {
      const merged = datasets.reduce((acc, cur) => ({ ...acc, ...cur }), {});
      dataSummary.push(...extractValuesAsArray(merged));
    });
    // console.log(results,'dataSummary');
    

    return dataSummary;
  } catch (error) {
    console.error("Error fetching or processing CSV summary:", error);
    throw error;
  }
});


// Function to read all constants from server
export const readAllConstants = memoizee(async function () {
  try {
    const [
      licenseClasses,
      customLicenseClasses,
      languageGroups,
      taskGroups,
      source_groups,
      modelGroups,
      creatorGroups,
      formats,
    ] = await Promise.all([
      axios.get(CONSTANTS.LICENSE_CONSTANTS_URL),
      axios.get(CONSTANTS.CUSTOM_LICENSE_CONSTANTS_URL),
      axios.get(CONSTANTS.LANGUAGE_CONSTANTS_URL),
      axios.get(CONSTANTS.TASK_CONSTANTS_URL),
      axios.get(CONSTANTS.DOMAINS_CONSTANTS_URL),
      axios.get(CONSTANTS.MODEL_CONSTANTS_URL),
      axios.get(CONSTANTS.CREATOR_CONSTANTS_URL),
      axios.get(CONSTANTS.FORMATS_CONSTANTS_URL),
    ]);

    return {
      LICENSE_CLASSES: licenseClasses.data,
      CUSTOM_LICENSE_CLASSES: customLicenseClasses.data,
      LANGUAGE_GROUPS: languageGroups.data,
      TASK_GROUPS: taskGroups.data,
      DOMAIN_GROUPS: source_groups.data,
      MODEL_GROUPS: modelGroups.data,
      CREATOR_GROUPS: creatorGroups.data,
      FORMATS: formats.data,
    };
  } catch (error) {
    console.error("Error fetching constants:", error);
    return {};
  }
});

export { readDataSummaryJson };
